import { createElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { landingMaxBins, useFilterLandingBinStore } from "stores/filterLandingBinStore";

import { setUpdatePlayLegend } from "store/features";

import { IGroupBy, LegendItemModel, WellListFilterModel } from "models";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

import {
  StyledCheckBoxEmptyIcon,
  StyledCheckBoxIcon,
  StyledExcludeIcon
} from "../shared";
import { LegendContextMenuItem } from "../styled";
import { BatchAction, GroupByFilterDependencies } from "../types";
import { getLegendFilter, getOthersRollupFilter } from "../util";
import { checkedBinCount, exceedsMaxBins } from "../util/checkedBins";
import { getValidResourcePlays } from "../util/getValidResourcePlays";

// EVA-6412 RESHAPE (T019) — consumes the resolved `deps` object; drops the
// entityKind === Well branches in updateSelectedLegendItems/
// addCheckedItemsToFilter/the setHasCheckedBins effect (fixes P2). The
// Legend selection persists across remounts (module scope), but keyed BY ENTITY
// KIND: Home.tsx mounts a Well and a Facility GroupByFilterActivity
// concurrently, so a single shared array would let one panel's selection bleed
// into the other's. Keying by kind gives per-kind persistence with no
// cross-instance bleed — the DQ-3 resolution (research.md R7).
const outerSelectedLegendItemsByKind: Record<string, LegendItemModel[]> = {};

export interface CheckedItemFilteringControllerProps {
  groupBy: IGroupBy;
  wellListFilter: Maybe<WellListFilterModel[]>;
}

export interface CheckedItemFilteringControllerResult {
  checkedLegendItemsRef: React.MutableRefObject<LegendItemModel[]>;
  selectedLegendItems: LegendItemModel[];
  updateSelectedLegendItems: (items: LegendItemModel[]) => void;
  addItemToChecked: (item: LegendItemModel) => void;
  addManyItemsToChecked: (items: LegendItemModel[], shouldLock?: boolean) => void;
  replacePlayChecked: (item: LegendItemModel) => void;
  addCheckedItemsToFilter: (shouldLock?: boolean) => void;
  removeSelectedLegendItemsFromFilter: (items: LegendItemModel[]) => void;
  invertSelectedLegendItemsFromFilter: (items: LegendItemModel[]) => void;
  addSelectedLegendItemsToFilter: (items: LegendItemModel[], equal: boolean) => void;
  checkOnlySelected: (items: LegendItemModel[], equal: boolean) => void;
  performBatchOption: (action: BatchAction, collapsedItems?: LegendItemModel[]) => void;
  // Stable handlers Bin Settings / Legend call directly instead of the former
  // module-global rxjs bus — named for what triggers them (CONTEXT.md).
  onBinsChanged: () => void;
  onDateRangeChanged: () => void;
  // Private-latest-accessor setter (useLatest pattern): Legend calls this
  // whenever its filtered list changes so batch ops here can read the CURRENT
  // filtered legend at click-time. Deliberately NOT a ref on the interface —
  // see filteredItemsRef below, which stays fully internal to this module.
  reportFilteredItems: (items: LegendItemModel[]) => void;
  // Bumped whenever checking items should auto-lock the bins. Bin Settings
  // observes this value and performs the actual lock (bin-locking is its own
  // domain) — this keeps the dependency one-directional (Bin Settings is
  // composed after this hook) instead of a ref-trampoline back into it.
  autoLockRequestId: number;
  autoLockCheckedCount: number;
  contextMenuItems: {
    name: string;
    icon: JSX.Element;
    disabled?: boolean;
    onClick: () => void;
  }[];
  ContextMenuItemsComponent: (props: {
    onSelect: (v: { onClick: () => void }) => void;
  }) => JSX.Element;
}

export const useCheckedItemFilteringController = (
  deps: GroupByFilterDependencies,
  { groupBy, wellListFilter }: CheckedItemFilteringControllerProps
): CheckedItemFilteringControllerResult => {
  const dispatch = useDispatch();
  const selectionKey = deps.isWell ? "well" : "facility";
  const checkedLegendItemsRef = useRef<LegendItemModel[]>([]);
  const [selectedLegendItems, setSelectedLegendItems] = useState(
    outerSelectedLegendItemsByKind[selectionKey] ?? []
  );

  // Private latest-accessor for Legend's filtered list (useLatest pattern) —
  // batch ops (check-all/uncheck-all/invert) need the CURRENT filtered legend
  // at click-time. Fed via reportFilteredItems (an ordinary callback), never
  // exposed as a ref on this hook's interface.
  const filteredItemsRef = useRef<LegendItemModel[]>([]);
  const reportFilteredItems = useCallback((items: LegendItemModel[]) => {
    filteredItemsRef.current = items;
  }, []);

  const [autoLockRequestId, setAutoLockRequestId] = useState(0);
  const [autoLockCheckedCount, setAutoLockCheckedCount] = useState(0);
  const requestAutoLock = useCallback((checkedCount: number) => {
    setAutoLockCheckedCount(checkedCount);
    setAutoLockRequestId((n) => n + 1);
  }, []);
  // A bulk check can take the checked values past MaxBins too, so it asks Bin
  // Settings to lock and fit them, as a single check does.
  const requestLockToFit = useCallback(
    (shouldLock: boolean) => {
      const checkedCount = checkedBinCount(
        checkedLegendItemsRef.current,
        deps.groupBy.property
      );
      // A bin still waiting for the well filter is the one these checks are grouped by.
      const maxBins = deps.isWell
        ? landingMaxBins(deps.groupBy.property, deps.groupBy.bin?.MaxBins)
        : deps.groupBy.bin?.MaxBins;
      if (shouldLock || exceedsMaxBins(maxBins, checkedCount)) {
        requestAutoLock(checkedCount);
      }
    },
    [deps.isWell, deps.groupBy.property, deps.groupBy.bin?.MaxBins, requestAutoLock]
  );

  const {
    mapViewLocked,
    setViewLock,
    setSelectedGroups,
    addManyPropertiesToFilter,
    setHasCheckedBins
  } = deps;

  const updateSelectedLegendItems = useCallback(
    (items: LegendItemModel[]) => {
      setSelectedGroups(items.map((s) => s?.title).filter((f) => f && f !== ""));
      setSelectedLegendItems(items);
      outerSelectedLegendItemsByKind[selectionKey] = items;
    },
    [setSelectedGroups, selectionKey]
  );

  const addCheckedItemsToFilter = useCallback(
    (shouldLock?: boolean) => {
      if (!checkedLegendItemsRef.current) {
        return;
      }

      const legendFilters = checkedLegendItemsRef.current.map((item) => {
        if (item.retainedFilter) {
          // Copied: the filter came from frozen store state and the write re-ids it.
          return {
            ...item.retainedFilter,
            predicates: item.retainedFilter.predicates.map((p) => ({ ...p }))
          };
        }
        const pdenSource =
          typeof groupBy.pdenSource === "string"
            ? PdenSourceEnum[groupBy.pdenSource]
            : parseInt(groupBy.pdenSource);
        // Use shouldLock parameter to determine IsLocked value
        // This handles the case where toggleLockBins hasn't updated Redux yet
        const binWithCorrectLock = shouldLock
          ? { ...item.bin, IsLocked: true }
          : item.bin;
        if (item.isOthersRollup) {
          return getOthersRollupFilter(
            item.property,
            item.categoryId,
            binWithCorrectLock,
            item.isNotEqual,
            pdenSource,
            item.groupByTitle,
            item.partialContextTitle,
            item.forecastTrumpingSettings,
            item.performanceDateRange
          );
        }
        return getLegendFilter(
          item.property,
          item.canBin,
          [item].map((item) => item.title),
          item.categoryId,
          binWithCorrectLock,
          item.normalizeBySettings,
          item.isNotEqual,
          pdenSource,
          item.value,
          item.groupByTitle,
          item.partialContextTitle,
          item.isForecastToggleOn,
          item.selectedForecastFolderName,
          item.forecastTrumpingSettings,
          item.performanceDateRange
        );
      });
      const isTextField = groupBy.dataType.toLowerCase() === "text";
      const hasQuantileBinFilter =
        checkedLegendItemsRef.current.filter((item) => item.bin.BinType == "Quantile")
          .length > 0 && !isTextField;

      //Lock the map if filtering by quantile because we do not want the distribution to change
      setViewLock(hasQuantileBinFilter || mapViewLocked);
      addManyPropertiesToFilter({ legendFilters, propertyName: groupBy.property });
    },
    [
      mapViewLocked,
      setViewLock,
      addManyPropertiesToFilter,
      groupBy.pdenSource,
      groupBy.property
    ]
  );

  const addManyItemsToChecked = useCallback(
    (items: LegendItemModel[], shouldLock?: boolean) => {
      for (const item of items) {
        //remove from item
        const index = checkedLegendItemsRef.current.findIndex(
          (x) => x.property === item.property && x.value === item.value
        );
        if (index >= 0) {
          checkedLegendItemsRef.current.splice(index, 1);
        }
        if (item.isChecked) {
          checkedLegendItemsRef.current.push(item);
        }
      }
      //re-apply the checked set as a property filter now that it changed
      addCheckedItemsToFilter(shouldLock);
    },
    [addCheckedItemsToFilter]
  );

  const addItemToChecked = useCallback(
    (item: LegendItemModel) => {
      //remove from item
      if (!checkedLegendItemsRef.current || !item) {
        return;
      }
      const index = checkedLegendItemsRef.current.findIndex(
        (x) => x.property === item.property && x.value === item.value
      );
      if (index >= 0) {
        checkedLegendItemsRef.current.splice(index, 1);
      }
      if (item.isChecked) {
        checkedLegendItemsRef.current.push(item);
      }

      // Update the selected Resource Play for XDA projection
      const checkedResourcePlays = getValidResourcePlays(checkedLegendItemsRef.current);
      if (checkedResourcePlays.length === 1) {
        dispatch(setUpdatePlayLegend(checkedResourcePlays[0].value));
      } else {
        // Clear when 0 or 2+ Resource Plays are checked
        dispatch(setUpdatePlayLegend(""));
      }

      //re-apply the checked set as a property filter now that it changed
      addCheckedItemsToFilter(true);
    },
    [dispatch, addCheckedItemsToFilter]
  );

  const replacePlayChecked = useCallback(
    (item: LegendItemModel) => {
      checkedLegendItemsRef.current = checkedLegendItemsRef.current.filter(
        (x) => x.property !== "Header.ResourcePlay"
      );
      const index = checkedLegendItemsRef.current.findIndex(
        (x) => x.property === item.property && x.value === item.value
      );
      if (index >= 0) {
        checkedLegendItemsRef.current.splice(index, 1);
      }
      if (item.isChecked) {
        checkedLegendItemsRef.current.push(item);
      }
      addCheckedItemsToFilter(true);
    },
    [addCheckedItemsToFilter]
  );

  // Self-contained — only touch this hook's own addManyItemsToChecked primitive.
  const removeSelectedLegendItemsFromFilter = useCallback(
    (items: LegendItemModel[]) => {
      const selected = [...items];
      for (const item of selected) {
        item.isChecked = false;
        item.isNotEqual = false;
      }
      addManyItemsToChecked(selected);
    },
    [addManyItemsToChecked]
  );

  const invertSelectedLegendItemsFromFilter = useCallback(
    (items: LegendItemModel[]) => {
      const selected = [...items];
      for (const item of selected) {
        if (item.isNotEqual || item.isOthersRollup) continue;
        item.isChecked = !item.isChecked;
      }
      addManyItemsToChecked(selected);
    },
    [addManyItemsToChecked]
  );

  // "Bins changed"/"date-range changed" → uncheck the currently-filtered
  // legend AND force-clear the whole checked set (even entries outside the
  // current filtered list, since a property/bin-type change invalidates them
  // all), then re-apply the (now empty) checked set as the property filter.
  const uncheckAllAndReapplyFilter = useCallback(() => {
    removeSelectedLegendItemsFromFilter(filteredItemsRef.current);
    checkedLegendItemsRef.current = [];
    addCheckedItemsToFilter(undefined);
  }, [removeSelectedLegendItemsFromFilter, addCheckedItemsToFilter]);

  const onBinsChanged = useCallback(() => {
    uncheckAllAndReapplyFilter();
  }, [uncheckAllAndReapplyFilter]);

  const onDateRangeChanged = useCallback(() => {
    uncheckAllAndReapplyFilter();
  }, [uncheckAllAndReapplyFilter]);

  // ---- Coupled batch/lock orchestration (moved from the controller in the P1
  // thin-controller follow-up). The map/bin view-lock atomic with checking a
  // Quantile filter is this hook's own domain (CONTEXT.md); the actual bin
  // lock TOGGLE lives in Bin Settings, so it is requested via
  // requestAutoLock/autoLockRequestId rather than called directly.
  const addSelectedLegendItemsToFilter = useCallback(
    (items: LegendItemModel[], equal: boolean) => {
      if (!equal) {
        deps.setSelectedGroups([]);
      }
      // Others can only be excluded, so an include leaves it out.
      const selected = items.filter((item) => !!item && (!equal || !item.isOthersRollup));
      for (const item of selected) {
        item.isChecked = true;
        item.isNotEqual = !equal;
        item.normalizeBySettings = Object.assign({}, deps.normalizeBy, {
          useNormalizeBy: deps.useNormalizeBy
        });
      }
      const shouldLock = !deps.groupBy.bin?.IsLocked;
      addManyItemsToChecked(selected, shouldLock || deps.groupBy.bin?.IsLocked);
      requestLockToFit(shouldLock);
    },
    [deps, addManyItemsToChecked, requestLockToFit]
  );

  const checkOnlySelected = useCallback(
    (items: LegendItemModel[], equal: boolean) => {
      const selected = items.filter((item) => !!item && (!equal || !item.isOthersRollup));
      removeSelectedLegendItemsFromFilter(checkedLegendItemsRef.current);
      for (const item of selected) {
        item.isChecked = true;
        item.isNotEqual = !equal;
        item.normalizeBySettings = Object.assign({}, deps.normalizeBy, {
          useNormalizeBy: deps.useNormalizeBy
        });
      }
      const shouldLock =
        !deps.groupBy.bin?.IsLocked &&
        (deps.groupBy.bin?.UseDynamicBins || !deps.groupBy.bin?.BinSize);
      addManyItemsToChecked(selected, shouldLock || deps.groupBy.bin?.IsLocked);
      requestLockToFit(shouldLock);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deps, addManyItemsToChecked, removeSelectedLegendItemsFromFilter, requestLockToFit]
  );

  const performBatchOption = useCallback(
    (action: BatchAction, collapsedItems: LegendItemModel[] = []) => {
      if (action === "checkAll") {
        addSelectedLegendItemsToFilter(filteredItemsRef.current, true);
      } else if (action === "uncheckAll") {
        // Checked values collapsed into Others count as checked but have no row to filter.
        removeSelectedLegendItemsFromFilter([
          ...filteredItemsRef.current,
          ...collapsedItems
        ]);
      } else if (action === "invertChecks") {
        invertSelectedLegendItemsFromFilter(filteredItemsRef.current);
      }
    },
    [
      addSelectedLegendItemsToFilter,
      removeSelectedLegendItemsFromFilter,
      invertSelectedLegendItemsFromFilter
    ]
  );

  // Only the Others row is selected: it can be excluded and nothing else.
  const onlyOthersSelected =
    selectedLegendItems.length > 0 &&
    selectedLegendItems.every((item) => item?.isOthersRollup);

  const contextMenuItems = useMemo(
    () => [
      {
        name: "Check",
        icon: createElement(StyledCheckBoxIcon),
        disabled: onlyOthersSelected,
        onClick: () => addSelectedLegendItemsToFilter(selectedLegendItems, true)
      },
      {
        name: "Check Only Selected",
        icon: createElement(StyledCheckBoxIcon),
        disabled: onlyOthersSelected,
        onClick: () => checkOnlySelected(selectedLegendItems, true)
      },
      {
        name: "Uncheck",
        icon: createElement(StyledCheckBoxEmptyIcon),
        disabled: onlyOthersSelected,
        onClick: () => removeSelectedLegendItemsFromFilter(selectedLegendItems)
      },
      {
        name: "Exclude",
        icon: createElement(StyledExcludeIcon),
        onClick: () => addSelectedLegendItemsToFilter(selectedLegendItems, false)
      }
    ],
    [
      selectedLegendItems,
      onlyOthersSelected,
      addSelectedLegendItemsToFilter,
      checkOnlySelected,
      removeSelectedLegendItemsFromFilter
    ]
  );

  // Hoisted out of render (INV-A2) — no inline component definition; built via
  // createElement since this composition file is .ts (no JSX transform) and
  // project convention forbids `import React from "react"`.
  const ContextMenuItemsComponent = useMemo(() => {
    const Component = ({
      onSelect
    }: {
      onSelect: (v: { onClick: () => void }) => void;
    }) =>
      createElement(
        "div",
        null,
        ...contextMenuItems.map((item) =>
          createElement(
            LegendContextMenuItem,
            {
              key: item.name,
              "aria-disabled": item.disabled ? true : undefined,
              onClick: () => {
                if (item.disabled) return;
                onSelect(item as never);
                item.onClick();
              }
            },
            item.icon,
            " ",
            item.name
          )
        )
      );
    return Component;
  }, [contextMenuItems]);

  useEffect(() => {
    // While a saved filter is in flight its flag goes out as it lands (landWellFilterId).
    if (deps.isWell && useFilterLandingBinStore.getState().isAwaitingFilter) return;
    setHasCheckedBins(checkedLegendItemsRef.current?.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedLegendItemsRef.current, setHasCheckedBins]);

  useEffect(() => {
    if (wellListFilter?.length !== 0) {
      // clear the checked legend items when a well list is pasted
      checkedLegendItemsRef.current = [];
    }
  }, [wellListFilter]);

  return {
    checkedLegendItemsRef,
    selectedLegendItems,
    updateSelectedLegendItems,
    addItemToChecked,
    addManyItemsToChecked,
    replacePlayChecked,
    addCheckedItemsToFilter,
    removeSelectedLegendItemsFromFilter,
    invertSelectedLegendItemsFromFilter,
    addSelectedLegendItemsToFilter,
    checkOnlySelected,
    performBatchOption,
    onBinsChanged,
    onDateRangeChanged,
    reportFilteredItems,
    autoLockRequestId,
    autoLockCheckedCount,
    contextMenuItems: contextMenuItems as never,
    ContextMenuItemsComponent: ContextMenuItemsComponent as never
  };
};

export { useCheckedItemFilteringController as default };
